import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import PureImage from "../Image/PureImage"
import { eImageVersions } from "../../definitions/Image"
import { useImageUrl } from "../../hooks/useImageUrl"
import { get } from "../../store/data/actions"
import { iFile } from "../../definitions/File"

import s from "./Upload.less"
import Video from "../Video"
import { eVideoVersions } from "../../definitions/Video"
import { useVideoUrl } from "../../hooks/useVideoUrl"
import Loader from "../Loader/Loader"
import Audio from "../Audio"
import { useAudioUrl } from "../../hooks/useAudioUrl"
import { eAudioVersions } from "../../definitions/Audio"

interface iProps {
  fileCollection: string
  fileId: string
  removeFile: (_id: string) => void
}

const OldFile: FunctionComponent<iProps> = ({
  fileCollection,
  fileId,
  removeFile,
}) => {
  const [file, setFile] = useState<iFile>()
  const [pingForFile, setPing] = useState(false)
  const _interval = useRef<NodeJS.Timeout>()
  const _shouldPing = useRef(false)
  const _pingTime = useRef(5000)

  useMemo(() => {
    if (fileId) {
      get<iFile>({
        link: "/v1/file/",
        query: { collection: fileCollection, _id: fileId },
      }).then(({ versions, ...rest }) => {
        setFile({ versions, ...rest, _id: fileId })
      })
    }
  }, [fileId])

  useEffect(() => {
    _shouldPing.current = pingForFile

    const intervalFunction = () => {
      if (fileId) {
        get<iFile>({
          link: "/v1/file/",
          query: { collection: fileCollection, _id: fileId },
        }).then(({ versions, ...rest }) => {
          setFile({ versions, ...rest, _id: fileId })
          _interval.current && clearInterval(_interval.current)
          if (_shouldPing.current) {
            _pingTime.current *= 2
            _interval.current = setInterval(intervalFunction, _pingTime.current)
          }
        })
      }
    }

    if (_shouldPing.current) {
      if (_interval.current) return
      _interval.current = setInterval(intervalFunction, _pingTime.current)
    } else {
      _interval.current && clearInterval(_interval.current)
    }
  }, [pingForFile])

  const handleRemoveFile = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    confirm("Are you sure you want to remove it?") &&
      removeFile(file?._id || "")
  }

  return (
    <div className={s.singleFile}>
      {file && (
        <Preview
          file={file}
          fileCollection={fileCollection}
          setPing={(val) => {
            if (val !== pingForFile) setPing(val)
          }}
        />
      )}
      <div className={s.info}>
        <div>
          <p>{file?.name}</p>
          <p>{Math.round((file?.size || 0) / 1024)} KB</p>
        </div>
        <div className={s.btnWrap}>
          <button
            className="btn btn-red"
            type="button"
            onClick={handleRemoveFile}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

export default OldFile

interface iPreviewProps {
  file: iFile
  fileCollection: string
  setPing: (shouldPing: boolean) => void
}

const Preview: FunctionComponent<iPreviewProps> = ({
  file,
  fileCollection,
  setPing,
}) => {
  const [url, setUrl] = useState("")
  if (!file) return null

  useEffect(() => {
    if (!url) {
      let newUrl = ""

      switch (fileCollection) {
        case "Files_Meshes":
          newUrl = "/icons/document.svg"
          break
        case "Files_Assets":
          if (file.type?.includes("image")) {
            newUrl = useImageUrl(eImageVersions.original, file.versions)
          } else {
            newUrl = "/icons/document.svg"
          }
          break
        case "Files_Images":
        case "Files_AssetPreview":
          newUrl = useImageUrl(eImageVersions.original, file.versions)
          break

        case "Files_Videos":
        case "Files_VideoAssets":
          newUrl = useVideoUrl(eVideoVersions.original, file.versions)
          break
        case "Files_TextToSpeech":
          newUrl = useAudioUrl(eAudioVersions.original, file.versions)
          break
        default:
          newUrl = useImageUrl(eImageVersions.LIcon, file.versions)
          break
      }
      if (!newUrl) {
        setPing(true)
      } else {
        setPing(false)
      }
      setUrl(newUrl)
    }
  }, [file, fileCollection])

  if (!url) {
    return (
      <div className={s.imageHolder}>
        <Loader />
      </div>
    )
  }

  switch (fileCollection) {
    case "Files_Videos":
    case "Files_VideoAssets":
      return <Video url={url} />

    case "Files_TextToSpeech":
      return <Audio autoplay={false} url={url} />
    default:
      return (
        <div className={s.imageHolder}>
          <PureImage
            onLoad={() => {
              setPing(false)
            }}
            src={url}
          />
        </div>
      )
  }
}
